/**
 * /saas/metrics — Standalone metrics history dashboard.
 *
 * Complements the inline Metrics tab on /saas by providing a dedicated
 * route with full-height snapshot history, prominent live MRR/ARR, and
 * the ability to take new snapshots.
 *
 * API surface used:
 *   GET  /api/saas/metrics/mrr          → { mrr: string }
 *   GET  /api/saas/metrics/arr          → { arr: string }
 *   GET  /api/saas/metrics/snapshots    → MetricsSnapshot[]
 *   POST /api/saas/metrics/snapshot     → MetricsSnapshot
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DataGridPro,
  type GridColDef,
} from "@mui/x-data-grid-pro";

import { FlexGrid, gridDefaults } from "@/grids/gridDefaults";
import { api } from "@/lib/api";
import {
  saasArrQueryOptions,
  saasKeys,
  saasMrrQueryOptions,
  saasPlansQueryOptions,
  saasSnapshotsQueryOptions,
  saasSubscriptionsQueryOptions,
  saasBurnQueryOptions,
  saasLtvCacQueryOptions,
  partiesListQueryOptions,
} from "@/lib/queries";
import { Link } from "@tanstack/react-router";
import { useToast } from "@/components/Toaster";
import type { MetricsSnapshot, Plan, Subscription } from "@/lib/types";

export const Route = createFileRoute("/_auth/saas/metrics")({
  component: SaasMetricsPage,
});

const LIMIT_OPTIONS = [30, 90, 180, 365] as const;

function fmtMoney(value: string): string {
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtChurnRate(value: string | null): string {
  if (!value) return "—";
  return `${(Number(value) * 100).toFixed(2)}%`;
}

function SaasMetricsPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const [limit, setLimit] = useState<90 | 30 | 180 | 365>(90);

  // Hash-scroll on mount — lets nav links like /saas/metrics#burn land on
  // the right tile. Runs after layout so the target id exists in the DOM.
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    // Defer to next tick so initial render has placed the target div.
    const t = window.setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    return () => window.clearTimeout(t);
  }, []);

  const mrrQuery = useQuery(saasMrrQueryOptions());
  const arrQuery = useQuery(saasArrQueryOptions());
  const snapshotsQuery = useQuery(saasSnapshotsQueryOptions(limit));
  const subsQuery = useQuery(saasSubscriptionsQueryOptions());
  const plansQuery = useQuery(saasPlansQueryOptions());
  const customersQuery = useQuery(partiesListQueryOptions({ party_type: "customer" }));

  // Trailing-12-month date range for LTV/CAC
  const [ltvEndDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [ltvStartDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  });

  const burnQuery = useQuery(saasBurnQueryOptions(12));
  const ltvCacQuery = useQuery(saasLtvCacQueryOptions(ltvStartDate, ltvEndDate));
  const vendorWatchQuery = useQuery({
    queryKey: ["vendor-watch", "accounts", "metrics-tile"],
    queryFn: () => api<{ accounts: any[]; count: number }>("/api/vendor-watch/accounts"),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const takeSnapshot = useMutation({
    mutationFn: async () =>
      api<MetricsSnapshot>("/api/saas/metrics/snapshot", { method: "POST" }),
    onSuccess: (snap) => {
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success(
        `Snapshot taken — MRR ${fmtMoney(snap.mrr)} ${snap.base_currency_code}`,
      );
    },
    onError: (err: Error) => toast.error(`Snapshot failed: ${err.message}`),
  });

  // Lookup maps
  const plansById = useMemo(() => {
    const m = new Map<string, Plan>();
    for (const p of plansQuery.data ?? []) m.set(p.id, p);
    return m;
  }, [plansQuery.data]);

  const customersById = useMemo(() => {
    const m = new Map<string, string>(); // id → name
    for (const c of customersQuery.data ?? []) m.set(c.id, c.name);
    return m;
  }, [customersQuery.data]);

  // Normalise plan amount to monthly MRR contribution per 1 unit
  function mrrPerUnit(plan: Plan): number {
    const amount = Number(plan.amount);
    const count = plan.interval_count || 1;
    switch (plan.interval) {
      case "day":     return amount * (30 / count);
      case "week":    return amount * (30 / (7 * count));
      case "month":   return amount / count;
      case "quarter": return amount / (3 * count);
      case "year":    return amount / (12 * count);
      default:        return amount;
    }
  }

  // Active subscriptions with enriched data
  const activeSubs = useMemo(() => {
    return (subsQuery.data ?? [])
      .filter((s) => s.status === "active" || s.status === "trialing" || s.status === "past_due")
      .map((sub) => {
        const customerName = customersById.get(sub.customer_id) ?? "Unknown";
        // Compute per-sub MRR across all items
        let totalSeats = 0;
        let isPerSeat = false;
        for (const item of sub.items) {
          const plan = plansById.get(item.plan_id);
          if (plan?.is_metered || item.quantity > 1) isPerSeat = true;
          totalSeats += item.quantity;
        }
        // MRR calc using the mrrPerUnit helper
        const subMrr = sub.items.reduce((acc, item) => {
          const plan = plansById.get(item.plan_id);
          if (!plan) return acc;
          const unitPrice = item.unit_amount_override
            ? Number(item.unit_amount_override)
            : Number(plan.amount);
          const baseMrr = mrrPerUnit({ ...plan, amount: String(unitPrice) });
          return acc + baseMrr * item.quantity;
        }, 0);
        return { sub, customerName, subMrr, totalSeats, isPerSeat };
      })
      .sort((a, b) => b.subMrr - a.subMrr);
  }, [subsQuery.data, plansById, customersById]);

  // Derive trend: latest snapshot vs second-latest
  const trend = useMemo(() => {
    const snaps = snapshotsQuery.data ?? [];
    if (snaps.length < 2) return null;
    const latest = Number(snaps[0].mrr);
    const prev = Number(snaps[1].mrr);
    if (prev === 0) return null;
    return ((latest - prev) / prev) * 100;
  }, [snapshotsQuery.data]);

  const columns = useMemo<GridColDef<MetricsSnapshot>[]>(() => [
    {
      field: "snapshot_date",
      headerName: "Date",
      width: 120,
    },
    {
      field: "mrr",
      headerName: "MRR",
      width: 160,
      renderCell: (p) => (
        <span className="font-mono text-sm">
          {fmtMoney(p.row.mrr)}{" "}
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {p.row.base_currency_code}
          </span>
        </span>
      ),
    },
    {
      field: "arr",
      headerName: "ARR",
      width: 160,
      renderCell: (p) => (
        <span className="font-mono text-sm">
          {fmtMoney(p.row.arr)}{" "}
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {p.row.base_currency_code}
          </span>
        </span>
      ),
    },
    {
      field: "active_customers",
      headerName: "Active cust.",
      width: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
    },
    {
      field: "new_customers",
      headerName: "New",
      width: 80,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (p) =>
        p.row.new_customers > 0 ? (
          <span className="text-emerald-700 font-semibold">
            +{p.row.new_customers}
          </span>
        ) : (
          <span>{p.row.new_customers}</span>
        ),
    },
    {
      field: "churned_customers",
      headerName: "Churned",
      width: 90,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (p) =>
        p.row.churned_customers > 0 ? (
          <span className="text-red-600 font-semibold">
            -{p.row.churned_customers}
          </span>
        ) : (
          <span>{p.row.churned_customers}</span>
        ),
    },
    {
      field: "active_subscriptions",
      headerName: "Active subs",
      width: 110,
      type: "number",
      align: "right",
      headerAlign: "right",
    },
    {
      field: "trialing_subscriptions",
      headerName: "Trialing",
      width: 90,
      type: "number",
      align: "right",
      headerAlign: "right",
    },
    {
      field: "customer_churn_rate",
      headerName: "Churn rate",
      width: 110,
      align: "right",
      headerAlign: "right",
      renderCell: (p) => (
        <span
          className={
            p.row.customer_churn_rate && Number(p.row.customer_churn_rate) > 0.05
              ? "text-red-600 font-mono"
              : "font-mono"
          }
        >
          {fmtChurnRate(p.row.customer_churn_rate)}
        </span>
      ),
    },
  ], []);

  const latestSnap = snapshotsQuery.data?.[0];

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            SaaS Metrics
          </h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            MRR · ARR · Churn · Snapshot history
          </span>
          <div className="flex-1" />
          {snapshotsQuery.isFetching && (
            <span className="text-xs text-slate-400 dark:text-slate-500">Loading…</span>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4 flex flex-col gap-4">
        {/* Live KPI cards — row 1: revenue */}
        <div className="grid grid-cols-3 gap-4 shrink-0">
          <div className="bg-white border border-slate-200 rounded-lg p-5 dark:bg-gray-900 dark:border-gray-800">
            <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              Live MRR
            </div>
            <div className="text-3xl font-semibold text-slate-900 font-mono dark:text-slate-100">
              {mrrQuery.data ? fmtMoney(mrrQuery.data.mrr) : "—"}
            </div>
            {trend !== null && (
              <div
                className={`text-xs mt-1 font-medium ${
                  trend >= 0 ? "text-emerald-600" : "text-red-600"
                }`}
              >
                {trend >= 0 ? "+" : ""}
                {trend.toFixed(2)}% vs previous snapshot
              </div>
            )}
            <div className="text-xs text-slate-500 mt-1 dark:text-slate-400">
              Computed from active subscriptions, normalized to monthly.
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5 dark:bg-gray-900 dark:border-gray-800">
            <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              Live ARR
            </div>
            <div className="text-3xl font-semibold text-slate-900 font-mono dark:text-slate-100">
              {arrQuery.data ? fmtMoney(arrQuery.data.arr) : "—"}
            </div>
            <div className="text-xs text-slate-500 mt-1 dark:text-slate-400">
              MRR × 12. Does not account for one-time charges.
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5 dark:bg-gray-900 dark:border-gray-800">
            <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              Latest snapshot
            </div>
            {latestSnap ? (
              <>
                <div className="text-2xl font-semibold text-slate-900 font-mono dark:text-slate-100">
                  {latestSnap.active_customers}
                  <span className="text-base text-slate-500 ml-1 dark:text-slate-400">
                    active cust.
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                  {latestSnap.snapshot_date} · {latestSnap.trialing_subscriptions} trialing ·{" "}
                  {latestSnap.churned_customers > 0 ? (
                    <span className="text-red-600">
                      {latestSnap.churned_customers} churned
                    </span>
                  ) : (
                    "0 churned"
                  )}
                </div>
              </>
            ) : (
              <div className="text-slate-500 text-sm dark:text-slate-400">No snapshots yet</div>
            )}
          </div>
        </div>

        {/* KPI cards — row 2: burn, LTV/CAC, AI vendor spend */}
        <div className="grid grid-cols-3 gap-4 shrink-0">
          {/* Burn Rate / Runway */}
          <div id="burn" className="bg-white border border-slate-200 rounded-lg p-5 dark:bg-gray-900 dark:border-gray-800 scroll-mt-20">
            <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              Net Burn / Runway
            </div>
            {burnQuery.isLoading ? (
              <div className="text-slate-400 text-sm dark:text-slate-500">Loading…</div>
            ) : burnQuery.data?.headline ? (
              <>
                <div className="text-3xl font-semibold font-mono dark:text-slate-100"
                     style={{ color: (burnQuery.data.headline.net_burn_cents ?? 0) > 0 ? "#dc2626" : "#15803d" }}>
                  {burnQuery.data.headline.net_burn_cents !== null
                    ? `$${Math.round(Math.abs(burnQuery.data.headline.net_burn_cents) / 100).toLocaleString()}`
                    : "—"}
                  <span className="text-sm font-normal text-slate-500 ml-1">/mo</span>
                </div>
                <div className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                  {burnQuery.data.headline.runway_months !== null
                    ? `${burnQuery.data.headline.runway_months.toFixed(1)} mo runway · `
                    : "Cash-positive · "}
                  {burnQuery.data.headline.snapshot_month}
                </div>
              </>
            ) : (
              <div className="text-slate-400 text-sm dark:text-slate-500">
                No burn data — take a snapshot first
              </div>
            )}
          </div>

          {/* LTV / CAC */}
          <div id="ltv-cac" className="bg-white border border-slate-200 rounded-lg p-5 dark:bg-gray-900 dark:border-gray-800 scroll-mt-20">
            <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              LTV / CAC
            </div>
            {ltvCacQuery.isLoading ? (
              <div className="text-slate-400 text-sm dark:text-slate-500">Loading…</div>
            ) : ltvCacQuery.data ? (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-semibold font-mono text-slate-900 dark:text-slate-100">
                    {ltvCacQuery.data.ltv_cac_ratio > 0
                      ? `${ltvCacQuery.data.ltv_cac_ratio.toFixed(1)}×`
                      : "—"}
                  </span>
                  <span className="text-sm text-slate-500 dark:text-slate-400">ratio</span>
                </div>
                <div className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                  {ltvCacQuery.data.cac_payback_months > 0
                    ? `${ltvCacQuery.data.cac_payback_months.toFixed(1)} mo payback · `
                    : ""}
                  LTV ${ltvCacQuery.data.ltv.value > 0 ? Math.round(ltvCacQuery.data.ltv.value).toLocaleString() : "—"}
                  {ltvCacQuery.data.warnings.length > 0 && (
                    <span className="ml-1 text-amber-600" title={ltvCacQuery.data.warnings.join("; ")}>⚠</span>
                  )}
                </div>
              </>
            ) : ltvCacQuery.isError ? (
              <div className="text-slate-400 text-sm dark:text-slate-500">
                Tag GL accounts with <code className="text-xs">saas_category</code> to enable
              </div>
            ) : (
              <div className="text-slate-400 text-sm">—</div>
            )}
          </div>

          {/* AI Vendor Spend */}
          <Link to="/api-spend" className="no-underline">
            <div className="bg-white border border-slate-200 rounded-lg p-5 hover:border-blue-300 hover:shadow-sm transition-shadow cursor-pointer dark:bg-gray-900 dark:border-gray-800 dark:hover:border-blue-700">
              <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                AI Vendor Spend MTD ↗
              </div>
              {vendorWatchQuery.isLoading ? (
                <div className="text-slate-400 text-sm dark:text-slate-500">Loading…</div>
              ) : (() => {
                const accounts = vendorWatchQuery.data?.accounts ?? [];
                const totalCents = accounts.reduce(
                  (s: number, a: any) => s + (a.latest_snapshot?.amount_cents ?? 0), 0,
                );
                const criticalCount = accounts.filter(
                  (a: any) => a.is_mission_critical && a.active_alerts?.some((al: any) => al.severity === "critical"),
                ).length;
                const anyAlerts = accounts.some((a: any) => a.active_alerts?.length > 0);
                return (
                  <>
                    <div className={`text-3xl font-semibold font-mono ${criticalCount > 0 ? "text-red-600" : "text-slate-900 dark:text-slate-100"}`}>
                      {accounts.length === 0
                        ? "—"
                        : `$${(totalCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                      {accounts.length === 0
                        ? "Set up monitoring →"
                        : criticalCount > 0
                          ? `⚠️ ${criticalCount} critical alert${criticalCount > 1 ? "s" : ""}`
                          : anyAlerts
                            ? `${accounts.length} account${accounts.length > 1 ? "s" : ""} · alerts active`
                            : `${accounts.length} account${accounts.length > 1 ? "s" : ""} · on track`}
                    </div>
                  </>
                );
              })()}
            </div>
          </Link>
        </div>

        {/* Active subscriptions breakdown */}
        <div className="shrink-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Active subscriptions
            {subsQuery.isFetching && (
              <span className="ml-2 text-slate-400 font-normal normal-case">Loading…</span>
            )}
          </div>
          {activeSubs.length === 0 && !subsQuery.isLoading ? (
            <div className="text-sm text-slate-400 dark:text-slate-500 italic">No active subscriptions.</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-gray-800 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                    <th className="text-left px-3 py-2 font-medium">Customer</th>
                    <th className="text-left px-3 py-2 font-medium">Plan</th>
                    <th className="text-left px-3 py-2 font-medium">Billing</th>
                    <th className="text-right px-3 py-2 font-medium">Seats / Units</th>
                    <th className="text-right px-3 py-2 font-medium">MRR contribution</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                  {activeSubs.map(({ sub, customerName, subMrr, totalSeats, isPerSeat }) => {
                    const firstPlan = plansById.get(sub.items[0]?.plan_id);
                    const planName = sub.items.length === 1
                      ? (firstPlan?.name ?? sub.items[0]?.plan_id)
                      : `${sub.items.length} plans`;
                    const statusColour =
                      sub.status === "active" ? "text-emerald-700 dark:text-emerald-400" :
                      sub.status === "trialing" ? "text-blue-600 dark:text-blue-400" :
                      "text-red-600 dark:text-red-400";
                    return (
                      <tr key={sub.id} className="bg-white dark:bg-gray-900 hover:bg-slate-50 dark:hover:bg-gray-800">
                        <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-200">
                          {customerName}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                          {planName}
                        </td>
                        <td className="px-3 py-2">
                          {isPerSeat ? (
                            <span className="inline-flex items-center rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 text-xs px-2 py-0.5 font-medium">
                              per seat
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-slate-300 text-xs px-2 py-0.5 font-medium">
                              flat rate
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-slate-700 dark:text-slate-300">
                          {isPerSeat ? totalSeats : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-slate-800 dark:text-slate-200">
                          {fmtMoney(String(subMrr))}{" "}
                          <span className="text-xs text-slate-400">{sub.currency_code}</span>
                        </td>
                        <td className={`px-3 py-2 capitalize text-xs font-medium ${statusColour}`}>
                          {sub.status.replace("_", " ")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {snapshotsQuery.data?.length ?? 0} snapshot
            {(snapshotsQuery.data?.length ?? 0) === 1 ? "" : "s"}
          </span>
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 dark:text-slate-400">Show:</span>
            {LIMIT_OPTIONS.map((opt) => (
              <button
                key={opt}
                onClick={() => setLimit(opt)}
                className={`text-xs px-2 py-0.5 rounded ${
                  limit === opt
                    ? "bg-blue-100 text-blue-700 font-semibold dark:bg-blue-900 dark:text-blue-300"
                    : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-gray-800"
                }`}
              >
                {opt}d
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <button
            onClick={() => takeSnapshot.mutate()}
            disabled={takeSnapshot.isPending}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium disabled:opacity-50"
            title="Compute today's MRR/ARR + customer counts and persist as a snapshot"
          >
            {takeSnapshot.isPending ? "Snapshotting…" : "Take snapshot"}
          </button>
        </div>

        {/* Snapshot history grid */}
        <div className="flex-1 min-h-0">
          <FlexGrid>
            <DataGridPro
              {...gridDefaults}
              rows={snapshotsQuery.data ?? []}
              columns={columns}
              getRowId={(r) => r.id}
              loading={snapshotsQuery.isLoading}
              density="compact"
              disableRowSelectionOnClick
              initialState={{
                sorting: { sortModel: [{ field: "snapshot_date", sort: "desc" }] },
              }}
            />
          </FlexGrid>
        </div>
      </div>
    </div>
  );
}
